/**
 * RouteBreakdown — the "where is my money going" table that renders
 * under the swap card. Shows each active split leg with its path id,
 * input share, output amount, and the chain of pools it traverses.
 *
 * This is the UI surface where the aggregator earns trust: users can
 * eyeball the split and see why the router is not just shoving
 * everything through a single pool.
 */

import type { QuoteResponse } from "@/lib/api";
import { formatPct } from "@/lib/format";

interface RouteBreakdownProps {
  quote: QuoteResponse | null;
  loading: boolean;
}

export function RouteBreakdown({ quote, loading }: RouteBreakdownProps) {
  if (!quote || loading) {
    return (
      <div className="mt-4 rounded-[--radius] border border-dashed border-[--border] bg-[--bg-elev] p-5 text-center text-sm text-[--text-subtle]" role="status" aria-live="polite">
        {loading ? "Solving…" : "Enter an amount to see the split"}
      </div>
    );
  }

  const active = quote.splits.filter((s) => BigInt(s.amountIn) > 0n);
  if (active.length === 0) {
    return (
      <div className="mt-4 rounded-[--radius] border border-dashed border-[--border] bg-[--bg-elev] p-5 text-center text-sm text-[--text-subtle]" role="status" aria-live="polite">
        No viable route found for this size.
      </div>
    );
  }

  const blockLabel = quote.blockNumber > 0 ? `block ${quote.blockNumber}` : "latest";

  return (
    <div className="mt-4 overflow-hidden rounded-[--radius] border border-[--border] bg-[--bg-elev]">
      <div className="flex items-center justify-between border-b border-[--border] px-4 py-2.5 text-[11px] uppercase tracking-[0.1em] text-[--text-subtle]">
        <span>Route split</span>
        <span className="num normal-case tracking-normal">
          uplift {formatPct(quote.splittingUpliftPct, 2)} · {blockLabel} ·{" "}
          {quote.elapsedMs}ms
        </span>
      </div>

      <ul>
        {active.map((leg) => (
          <li
            key={leg.pathId}
            className="grid gap-2 border-b border-[--border] px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium text-[--text]">
                <code className="rounded bg-[--bg-inset] px-1.5 py-0.5 text-[11px] text-[--text-muted]">
                  {leg.pathId}
                </code>
                <span className="num text-[--accent]">
                  {leg.sharePct.toFixed(1)}%
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-[--text-subtle]">
                {leg.pools.map((pool, i) => (
                  <span key={pool.id} className="inline-flex items-center gap-1">
                    <a
                      href={`https://etherscan.io/address/${pool.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[--text-muted] hover:text-[--text] underline-offset-2 hover:underline"
                      title={pool.label}
                    >
                      {pool.label}
                    </a>
                    {i < leg.pools.length - 1 && <span>→</span>}
                  </span>
                ))}
              </div>
            </div>

            <div className="num text-right text-[--text-muted] sm:min-w-[120px]">
              {leg.amountInFormatted} {quote.inputToken}
            </div>
            <div className="num text-right text-[--text] sm:min-w-[140px]">
              {leg.amountOutFormatted} {quote.outputToken}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
